import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Tooltip
} from '@mui/material';
import { Add, Edit, Delete, CalendarMonth, TrendingUp } from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';
import { useConfirm } from '../../../hooks/useConfirm';

const translations = {
  ar: {
    title: 'السنوات المالية',
    subtitle: 'إدارة الفترات المالية والسنوات المحاسبية',
    addFiscalYear: 'إضافة سنة مالية',
    editFiscalYear: 'تعديل سنة مالية',
    yearName: 'اسم السنة',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    status: 'الحالة',
    actions: 'الإجراءات',
    active: 'نشط',
    closed: 'مغلق',
    cancel: 'إلغاء',
    save: 'حفظ',
    deleteConfirm: 'هل أنت متأكد من حذف هذه السنة المالية؟',
    saveSuccess: 'تم حفظ السنة المالية بنجاح',
    saveError: 'فشل حفظ السنة المالية',
    deleteSuccess: 'تم حذف السنة المالية بنجاح',
    deleteError: 'فشل حذف السنة المالية',
    noData: 'لا توجد سنوات مالية',
    duration: 'المدة'
  },
  en: {
    title: 'Fiscal Years',
    subtitle: 'Manage financial periods and accounting years',
    addFiscalYear: 'Add Fiscal Year',
    editFiscalYear: 'Edit Fiscal Year',
    yearName: 'Year Name',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    actions: 'Actions',
    active: 'Active',
    closed: 'Closed',
    cancel: 'Cancel',
    save: 'Save',
    deleteConfirm: 'Are you sure you want to delete this fiscal year?',
    saveSuccess: 'Fiscal year saved successfully',
    saveError: 'Failed to save fiscal year',
    deleteSuccess: 'Fiscal year deleted successfully',
    deleteError: 'Failed to delete fiscal year',
    noData: 'No fiscal years found',
    duration: 'Duration'
  }
};

interface FiscalYear {
  fiscalPeriodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
}

export default function FiscalYearTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingYear, setEditingYear] = useState<FiscalYear | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    periodName: '',
    startDate: '',
    endDate: '',
    isClosed: false
  });

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const loadFiscalYears = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/FiscalPeriods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiscalYears(response.data);
    } catch (error) {
      console.error('Failed to load fiscal years:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (year?: FiscalYear) => {
    if (year) {
      setEditingYear(year);
      setFormData({
        periodName: year.periodName,
        startDate: year.startDate.split('T')[0],
        endDate: year.endDate.split('T')[0],
        isClosed: year.isClosed
      });
    } else {
      setEditingYear(null);
      setFormData({
        periodName: '',
        startDate: '',
        endDate: '',
        isClosed: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingYear(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingYear) {
        await axios.put(`${config.API_BASE_URL}/FiscalPeriods/${editingYear.fiscalPeriodId}`, 
          { ...editingYear, ...formData },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } else {
        await axios.post(`${config.API_BASE_URL}/FiscalPeriods`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setMessage({ type: 'success', text: t.saveSuccess });
      handleCloseDialog();
      loadFiscalYears();
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/FiscalPeriods/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: t.deleteSuccess });
      loadFiscalYears();
    } catch (error: any) {
      setMessage({ type: 'error', text: t.deleteError });
    }
  };

  const getDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return `${months} ${language === 'ar' ? 'شهر' : 'months'}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarMonth sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Add Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3d8ed9 0%, #00d4e6 100%)',
              boxShadow: '0 6px 20px rgba(79, 172, 254, 0.6)',
            }
          }}
        >
          {t.addFiscalYear}
        </Button>
      </Box>

      {/* Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(79, 172, 254, 0.1)' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.yearName}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.startDate}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.endDate}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.duration}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.status}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fiscalYears.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <TrendingUp sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography color="text.secondary">
                      {t.noData}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                fiscalYears.map((year, index) => (
                  <TableRow 
                    key={year.fiscalPeriodId}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(79, 172, 254, 0.05)' },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="600" color="primary">
                        {year.periodName}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(year.startDate).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell>{new Date(year.endDate).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getDuration(year.startDate, year.endDate)} 
                        size="small"
                        sx={{ bgcolor: 'rgba(79, 172, 254, 0.1)', color: '#4facfe', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={year.isClosed ? t.closed : t.active}
                        color={year.isClosed ? 'default' : 'success'}
                        size="small"
                        sx={{ minWidth: 80, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t.editFiscalYear}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(year)}
                            sx={{
                              bgcolor: 'rgba(79, 172, 254, 0.1)',
                              '&:hover': { bgcolor: 'rgba(79, 172, 254, 0.2)' }
                            }}
                          >
                            <Edit sx={{ fontSize: 18, color: '#4facfe' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.deleteConfirm}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(year.fiscalPeriodId)}
                            sx={{
                              bgcolor: 'rgba(244, 67, 54, 0.1)',
                              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' }
                            }}
                          >
                            <Delete sx={{ fontSize: 18, color: '#f44336' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {editingYear ? t.editFiscalYear : t.addFiscalYear}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={t.yearName}
              name="periodName"
              value={formData.periodName}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.startDate}
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date) => setFormData(prev => ({ ...prev, startDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' }))}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.endDate}
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date) => setFormData(prev => ({ ...prev, endDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' }))}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  slotProps={{ textField: { fullWidth: true, required: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
              </LocalizationProvider>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            {t.cancel}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              px: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3d8ed9 0%, #00d4e6 100%)',
              }
            }}
          >
            {t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}